import React, { useEffect } from "react";
import { useStationConnection } from "@/hooks/useStationConnection";
import { initSoundEffects } from "@/services/SoundEffects";
import type { Station } from "@/lib/api-client";
import { EmbedHeader } from "./EmbedHeader";
import { EmbedNowPlaying } from "./EmbedNowPlaying";
import { EmbedControls } from "./EmbedControls";
import { EmbedOfflineMessage } from "./EmbedOfflineMessage";

interface EmbedStationPlayerProps {
  station: Station;
  theme?: "light" | "dark";
}

export function EmbedStationPlayer({ station, theme = "dark" }: EmbedStationPlayerProps) {
  const {
    isConnected,
    isConnecting,
    error: connectionError,
    connect,
    disconnect,
    nowPlaying
  } = useStationConnection({
    station,
    autoConnect: true,
  });

  // Initialize sound effects
  useEffect(() => {
    initSoundEffects();
  }, []);

  const isLive = station.isActive && station.status === "live";
  const isDisconnected = station.status === "disconnected";

  // Use real-time Now Playing data if available, otherwise fall back to static data
  const displayNowPlaying = nowPlaying ? {
    track: nowPlaying.name,
    artist: nowPlaying.artist,
    album: nowPlaying.album,
  } : station.nowPlaying;

  // Theme classes
  const bgColor = theme === "dark" ? "bg-[#081506]" : "bg-gray-50";
  const textColor = theme === "dark" ? "text-[#6adf47]" : "text-gray-900";
  const mutedTextColor = theme === "dark" ? "text-[#4fa935]" : "text-gray-600";

  const stationUrl = `https://macrowave.co/${station.broadcaster.username}/${station.slug}`;

  return (
    <div className={`h-full flex flex-col ${bgColor} ${textColor} font-array p-4 relative`}>
      {/* Corner Link to Station */}
      <a
        href={stationUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={`fixed top-2 right-2 z-50 px-3 py-1.5 rounded-full text-xs font-bold transition-all hover:scale-105 ${
          theme === "dark"
            ? "bg-[#6adf47]/10 text-[#6adf47] border border-[#6adf47]/30 hover:bg-[#6adf47]/20"
            : "bg-gray-900/10 text-gray-900 border border-gray-900/30 hover:bg-gray-900/20"
        }`}
        style={{
          backdropFilter: "blur(4px)",
          WebkitBackdropFilter: "blur(4px)",
        }}
      >
        View on Macrowave ↗
      </a>

      {/* Header */}
      <EmbedHeader
        station={station}
        isLive={isLive}
        theme={theme}
      />

      {/* Main Content Area */}
      <div className="flex-1 flex items-center justify-center">
        {!isLive ? (
          <EmbedOfflineMessage
            isDisconnected={isDisconnected}
            theme={theme}
          />
        ) : (
          <div className="w-full space-y-4">
            {/* Now Playing */}
            <EmbedNowPlaying
              nowPlaying={displayNowPlaying}
              isConnected={isConnected}
              theme={theme}
            />

            {/* Listener Count */}
            {isConnected && (
              <div className={`text-center text-sm ${mutedTextColor}`}>
                {station.viewerCount} {station.viewerCount === 1 ? 'Listener' : 'Listeners'}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Controls */}
      <EmbedControls
        isLive={isLive}
        isConnected={isConnected}
        isConnecting={isConnecting}
        onConnect={connect}
        onDisconnect={disconnect}
        connectionError={connectionError}
        theme={theme}
      />
    </div>
  );
}
